/**
 * Drawdown Analysis Section - Expandable
 * Shows underwater chart, recovery time, and drawdown statistics
 * Critical for understanding downside risk
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronUp, TrendingDown } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';

interface DrawdownPoint {
  date: string;
  drawdown: number; // negative percentage
}

interface DrawdownPeriod {
  startDate: string;
  endDate: string;
  recoveryDate: string | null;
  maxDrawdown: number;
  duration: number; // days
  recoveryTime: number | null; // days
}

interface DrawdownAnalysisSectionProps {
  underwaterData: DrawdownPoint[];
  significantDrawdowns: DrawdownPeriod[];
  currentDrawdown: number | null;
  maxDrawdown: number | null;
  avgDrawdown: number | null;
  defaultExpanded?: boolean;
}

const DrawdownAnalysisSection: React.FC<DrawdownAnalysisSectionProps> = ({
  underwaterData,
  significantDrawdowns,
  currentDrawdown,
  maxDrawdown,
  avgDrawdown,
  defaultExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 shadow-lg rounded border border-gray-200">
          <p className="text-sm font-semibold text-gray-900 mb-1">{label}</p>
          <p className="text-sm text-red-600">
            Drawdown: {payload[0].value.toFixed(2)}%
          </p>
        </div>
      );
    }
    return null;
  };

  const getDrawdownSeverity = (drawdown: number | null): { color: string; label: string } => {
    if (drawdown == null) return { color: 'text-gray-400', label: 'N/A' };
    const absDrawdown = Math.abs(drawdown);
    if (absDrawdown < 5) return { color: 'text-green-600', label: 'Mild' };
    if (absDrawdown < 10) return { color: 'text-yellow-600', label: 'Moderate' };
    if (absDrawdown < 20) return { color: 'text-orange-600', label: 'Significant' };
    return { color: 'text-red-600', label: 'Severe' };
  };

  const currentSeverity = getDrawdownSeverity(currentDrawdown);
  const maxSeverity = getDrawdownSeverity(maxDrawdown);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between hover:bg-gray-50 rounded-lg p-2 -m-2 transition-colors"
      >
        <div className="flex items-center gap-3">
          <TrendingDown className="w-5 h-5 text-red-600" />
          <div className="text-left">
            <h4 className="text-lg font-semibold text-gray-900">Drawdown Analysis</h4>
            <p className="text-sm text-gray-600">
              Underwater chart and recovery time analysis
            </p>
          </div>
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-gray-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-600" />
        )}
      </button>

      {/* Expandable Content */}
      {isExpanded && (
        <div className="mt-6 space-y-6">
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Current Drawdown</div>
              <div className={`text-2xl font-bold ${currentSeverity.color}`}>
                {currentDrawdown != null ? `${currentDrawdown.toFixed(2)}%` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500 mt-1">{currentSeverity.label}</div>
            </div>
            <div className="bg-red-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Maximum Drawdown</div>
              <div className={`text-2xl font-bold ${maxSeverity.color}`}>
                {maxDrawdown != null ? `${maxDrawdown.toFixed(2)}%` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500 mt-1">{maxSeverity.label}</div>
            </div>
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-sm text-gray-600 mb-1">Average Drawdown</div>
              <div className="text-2xl font-bold text-gray-900">
                {avgDrawdown != null ? `${avgDrawdown.toFixed(2)}%` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500 mt-1">Typical decline</div>
            </div>
          </div>

          {/* Underwater Chart */}
          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Underwater Equity Curve</h5>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={underwaterData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="date"
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px' }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomTooltip />} />
                <ReferenceLine y={0} stroke="#6b7280" strokeDasharray="3 3" />
                <Area
                  type="monotone"
                  dataKey="drawdown"
                  stroke="#EF4444"
                  fill="#FEE2E2"
                  fillOpacity={0.6}
                  name="Drawdown"
                />
              </AreaChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-500 mt-2">
              Shows how far the portfolio has declined from its peak value at any point in time
            </p>
          </div>

          {/* Significant Drawdown Periods */}
          <div>
            <h5 className="text-sm font-semibold text-gray-900 mb-3">Significant Drawdown Periods</h5>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Start Date</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Max Drawdown</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Duration</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Recovery Time</th>
                    <th className="text-left py-2 px-3 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {significantDrawdowns.map((dd, index) => (
                    <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-2 px-3 text-gray-900">{dd.startDate}</td>
                      <td className="py-2 px-3 text-red-600 font-semibold">
                        {dd.maxDrawdown.toFixed(2)}%
                      </td>
                      <td className="py-2 px-3 text-gray-700">
                        {dd.duration} days
                      </td>
                      <td className="py-2 px-3 text-gray-700">
                        {dd.recoveryTime ? `${dd.recoveryTime} days` : '-'}
                      </td>
                      <td className="py-2 px-3">
                        {dd.recoveryDate ? (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Recovered
                          </span>
                        ) : (
                          <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                            Ongoing
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Insight */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <p className="text-sm text-gray-800">
              <strong>Risk Assessment:</strong> {
                maxDrawdown == null
                  ? 'Insufficient data to assess drawdown risk.'
                  : Math.abs(maxDrawdown) < 10
                  ? 'Portfolio has shown strong downside protection with limited drawdowns.'
                  : Math.abs(maxDrawdown) < 20
                  ? 'Moderate drawdowns within acceptable risk parameters.'
                  : 'Significant drawdowns observed. Consider reviewing risk management strategies.'
              }
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawdownAnalysisSection;
